<script setup lang="ts">
/**
 * Rows (people) × columns (days or weeks) with a capacity colour per cell and
 * optional bars (blocks) laid over the cells.
 *
 * Two uses, one component:
 * - **Deployment plan**: cells show how full a person is, blocks are the
 *   bookings. Blocks can be dragged to another day or person, or moved with
 *   Alt + arrow keys; a `locked` block stays put.
 * - **Utilisation heatmap**: the same grid without blocks — only the cell
 *   colours (and optionally a figure per cell).
 *
 * The grid computes nothing about capacity itself. The caller answers per
 * cell (`cell`) with the booked share (`load`, 1 = full) and whether the day
 * is non-working for that person; non-working columns (weekends, public
 * holidays) are flagged on the column. Non-working cells are hatched.
 *
 * Like `NxBoard`, a move is only emitted (`block-move`); the caller writes it
 * and passes new blocks. `canMove` is the drop guard.
 */
import { computed, nextTick, ref, useId } from 'vue';
import { LockClosedIcon } from '@blpde/nx-icons';
import { hueVars, NX_HUES, type NxHue } from '../lib/hue';
import {
  loadLevel,
  placeBlocks,
  toFigure,
  type NxResourceGridBlock,
  type NxResourceGridBlockMove,
  type NxResourceGridCell,
  type NxResourceGridColumn,
  type NxResourceGridFigure,
  type NxResourceGridKey,
  type NxResourceGridRow,
  type PlacedBlock,
} from './resourceGrid';

export type NxResourceGridLegendLabels = {
  free: string;
  low: string;
  medium: string;
  full: string;
  over: string;
  nonWorking: string;
};

const props = withDefaults(
  defineProps<{
    rows: NxResourceGridRow[];
    columns: NxResourceGridColumn[];
    /** Content of a cell; `null`/`undefined` draws an empty cell. */
    cell?: (
      row: NxResourceGridRow,
      column: NxResourceGridColumn
    ) => NxResourceGridCell | null | undefined;
    blocks?: NxResourceGridBlock[];
    /** Drop guard. Return `false` to refuse a block move. */
    canMove?: (move: NxResourceGridBlockMove) => boolean;
    /** Cells become buttons and emit `cell-click` (click, Enter, Space). */
    interactiveCells?: boolean;
    /** Caption of the row header column. */
    rowHeader?: string;
    ariaLabel?: string;
    /** Width of the row header column (CSS length). */
    rowHeaderWidth?: string;
    /** Minimum width of one column (CSS length). */
    columnMinWidth?: string;
    /** Show the colour legend under the grid. */
    legend?: boolean;
    legendLabels?: NxResourceGridLegendLabels;
    /** Summary column per row, e.g. the booked hours of the period. */
    rowSummary?: (row: NxResourceGridRow) => NxResourceGridFigure | string | null | undefined;
    /** Caption of the summary column. */
    summaryLabel?: string;
    /** Footer row per column, e.g. the support staffing of a day. */
    columnFooter?: (
      column: NxResourceGridColumn
    ) => NxResourceGridFigure | string | null | undefined;
    /** Row header of the footer row. */
    footerLabel?: string;
    /** Accessible suffix of a locked block. */
    lockedLabel?: string;
    /** Announcement when somebody tries to move a locked block. */
    lockedHint?: (block: string) => string;
    /** Announcement after a keyboard move. */
    movedLabel?: (block: string, row: string, column: string) => string;
    /** Announcement when the guard refused a move. */
    rejectedLabel?: (block: string) => string;
  }>(),
  {
    cell: undefined,
    blocks: () => [],
    canMove: undefined,
    interactiveCells: false,
    rowHeader: '',
    ariaLabel: undefined,
    rowHeaderWidth: 'calc(var(--nxt-size-unit) * 11)',
    columnMinWidth: 'calc(var(--nxt-size-unit) * 3.25)',
    legend: false,
    legendLabels: () => ({
      free: 'frei',
      low: 'unter 50 %',
      medium: 'unter 85 %',
      full: 'voll',
      over: 'überbucht',
      nonWorking: 'arbeitsfrei',
    }),
    rowSummary: undefined,
    summaryLabel: 'Summe',
    columnFooter: undefined,
    footerLabel: '',
    lockedLabel: 'gesperrt',
    lockedHint: (block: string) => `„${block}“ ist gesperrt und kann nicht umgeplant werden`,
    movedLabel: (block: string, row: string, column: string) =>
      `„${block}“ verschoben: ${row}, ${column}`,
    rejectedLabel: (block: string) => `„${block}“ kann dort nicht hin`,
  }
);

const emit = defineEmits<{
  'block-move': [move: NxResourceGridBlockMove];
  'block-move-rejected': [move: NxResourceGridBlockMove];
  /** Somebody tried to move a locked block (drag or Alt + arrow). */
  'block-locked': [block: NxResourceGridBlock];
  'block-open': [block: NxResourceGridBlock];
  'cell-click': [row: NxResourceGridRow, column: NxResourceGridColumn];
}>();

defineSlots<{
  'row-label'?(props: { row: NxResourceGridRow }): unknown;
  'column-label'?(props: { column: NxResourceGridColumn }): unknown;
  cell?(props: {
    row: NxResourceGridRow;
    column: NxResourceGridColumn;
    cell: NxResourceGridCell | null;
  }): unknown;
  block?(props: { block: NxResourceGridBlock }): unknown;
}>();

const gridId = useId();
const rootRef = ref<HTMLElement | null>(null);
const announcement = ref('');

const columnIndex = computed(
  () => new Map(props.columns.map((column, index) => [column.key, index]))
);

type LaidOutRow = {
  row: NxResourceGridRow;
  /** First CSS grid line of the row (the header takes line 1). */
  gridRow: number;
  lanes: number;
  placed: PlacedBlock[];
};

const layout = computed<LaidOutRow[]>(() => {
  let gridRow = 2;
  return props.rows.map((row) => {
    const own = props.blocks.filter((b) => b.rowId === row.id);
    const { placed, lanes } = placeBlocks(own, columnIndex.value, props.columns.length);
    const entry = { row, gridRow, lanes, placed };
    gridRow += lanes;
    return entry;
  });
});

function cellOf(row: NxResourceGridRow, column: NxResourceGridColumn): NxResourceGridCell | null {
  return props.cell?.(row, column) ?? null;
}

function cellClasses(row: NxResourceGridRow, column: NxResourceGridColumn) {
  const cell = cellOf(row, column);
  const level = loadLevel(cell?.load);
  return {
    [`level-${level}`]: level !== null,
    'is-non-working': column.nonWorking || cell?.nonWorking,
    'is-current': column.current,
    'is-drop-ok':
      dropCell.value?.rowId === row.id && dropCell.value.key === column.key && dropCell.value.ok,
    'is-drop-rejected':
      dropCell.value?.rowId === row.id && dropCell.value.key === column.key && !dropCell.value.ok,
  };
}

function cellLabel(row: NxResourceGridRow, column: NxResourceGridColumn): string {
  const cell = cellOf(row, column);
  const parts = [row.label, column.label];
  if (cell?.title) parts.push(cell.title);
  else if (cell?.text) parts.push(cell.text);
  if (column.nonWorking || cell?.nonWorking) parts.push(props.legendLabels.nonWorking);
  return parts.join(', ');
}

function blockLabel(block: NxResourceGridBlock): string {
  return block.locked ? `${block.label} (${props.lockedLabel})` : block.label;
}

/** Intent tones are classes; a category hue is bound as local properties. */
function isHue(tone: string | undefined): tone is NxHue {
  return !!tone && (NX_HUES as readonly string[]).includes(tone);
}

function blockToneClass(block: NxResourceGridBlock): string {
  return isHue(block.tone) ? 'has-hue' : `tone-${block.tone ?? 'brand'}`;
}

function refuseLocked(block: NxResourceGridBlock) {
  emit('block-locked', block);
  announcement.value = props.lockedHint(block.label);
}

const summaryOf = (row: NxResourceGridRow) => toFigure(props.rowSummary?.(row));
const footerOf = (column: NxResourceGridColumn) => toFigure(props.columnFooter?.(column));
const hasSummary = computed(() => !!props.rowSummary);
const hasFooter = computed(() => !!props.columnFooter);

/** Grid line of the footer row: right below the last lane of the last row. */
const footerGridRow = computed(() => {
  const last = layout.value[layout.value.length - 1];
  return last ? last.gridRow + last.lanes : 2;
});

function allowed(move: NxResourceGridBlockMove): boolean {
  if (move.block.locked) return false;
  return props.canMove ? props.canMove(move) !== false : true;
}

function isNoop(move: NxResourceGridBlockMove) {
  return move.rowId === move.block.rowId && move.start === move.block.start;
}

function commit(move: NxResourceGridBlockMove): boolean {
  if (isNoop(move)) return false;
  if (!allowed(move)) {
    emit('block-move-rejected', move);
    announcement.value = props.rejectedLabel(move.block.label);
    return false;
  }
  emit('block-move', move);
  return true;
}

// ── Pointer drag & drop ────────────────────────────────────────────────────
/** The dragged block and which of its columns the pointer grabbed it by. */
const dragging = ref<{ block: NxResourceGridBlock; grabOffset: number } | null>(null);
const dropCell = ref<{ rowId: NxResourceGridKey; key: string; ok: boolean } | null>(null);

function onBlockDragStart(event: DragEvent, placed: PlacedBlock) {
  // Locked blocks are draggable on purpose: the attempt is what says "locked".
  if (placed.block.locked) {
    event.preventDefault();
    refuseLocked(placed.block);
    return;
  }
  const el = event.currentTarget as HTMLElement;
  const rect = el.getBoundingClientRect();
  const perColumn = rect.width / placed.span || 1;
  const grabOffset = Math.max(
    0,
    Math.min(placed.span - 1, Math.floor((event.clientX - rect.left) / perColumn))
  );
  dragging.value = { block: placed.block, grabOffset };
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(placed.block.id));
  }
}

function onDragEnd() {
  dragging.value = null;
  dropCell.value = null;
}

function moveForCell(row: NxResourceGridRow, columnIdx: number): NxResourceGridBlockMove | null {
  const drag = dragging.value;
  if (!drag) return null;
  const startIdx = Math.max(0, columnIdx - drag.grabOffset);
  const start = props.columns[startIdx]?.key;
  if (start === undefined) return null;
  return { block: drag.block, rowId: row.id, start };
}

function onCellDragOver(event: DragEvent, row: NxResourceGridRow, columnIdx: number) {
  const move = moveForCell(row, columnIdx);
  if (!move) return;
  const ok = isNoop(move) || allowed(move);
  dropCell.value = { rowId: row.id, key: move.start, ok };
  if (ok) {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }
}

function onCellDrop(row: NxResourceGridRow, columnIdx: number) {
  const move = moveForCell(row, columnIdx);
  onDragEnd();
  if (move) commit(move);
}

/** A block lies over cells; hovering it while dragging targets the cell under the pointer. */
function onBlockDragOver(event: DragEvent, placed: PlacedBlock, laid: LaidOutRow) {
  if (!dragging.value) return;
  const el = event.currentTarget as HTMLElement;
  const rect = el.getBoundingClientRect();
  const offset = Math.floor((event.clientX - rect.left) / (rect.width / placed.span || 1));
  onCellDragOver(event, laid.row, placed.column + Math.max(0, Math.min(placed.span - 1, offset)));
}

function onBlockDrop(event: DragEvent, placed: PlacedBlock, laid: LaidOutRow) {
  const el = event.currentTarget as HTMLElement;
  const rect = el.getBoundingClientRect();
  const offset = Math.floor((event.clientX - rect.left) / (rect.width / placed.span || 1));
  onCellDrop(laid.row, placed.column + Math.max(0, Math.min(placed.span - 1, offset)));
}

// ── Keyboard ───────────────────────────────────────────────────────────────
function focusBlock(id: NxResourceGridKey) {
  const blocks = rootRef.value?.querySelectorAll<HTMLElement>('[data-grid-block]') ?? [];
  for (const el of blocks) {
    if (el.dataset.gridBlock === String(id)) {
      el.focus();
      return;
    }
  }
}

async function onBlockKeydown(event: KeyboardEvent, placed: PlacedBlock) {
  const { block } = placed;
  if (event.key === 'Enter') {
    event.preventDefault();
    emit('block-open', block);
    return;
  }
  if (!event.altKey) return;
  const rowIdx = props.rows.findIndex((r) => r.id === block.rowId);
  let targetRow = rowIdx;
  let targetCol = placed.column;
  if (event.key === 'ArrowLeft') targetCol -= 1;
  else if (event.key === 'ArrowRight') targetCol += 1;
  else if (event.key === 'ArrowUp') targetRow -= 1;
  else if (event.key === 'ArrowDown') targetRow += 1;
  else return;
  event.preventDefault();
  if (block.locked) {
    refuseLocked(block);
    return;
  }
  const row = props.rows[targetRow];
  const column = props.columns[targetCol];
  if (!row || !column) return;
  const move = { block, rowId: row.id, start: column.key };
  if (!commit(move)) return;
  announcement.value = props.movedLabel(block.label, row.label, column.label);
  await nextTick();
  focusBlock(block.id);
}

function onCellKeydown(event: KeyboardEvent, row: NxResourceGridRow, column: NxResourceGridColumn) {
  if (!props.interactiveCells) return;
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  emit('cell-click', row, column);
}

function onCellClick(row: NxResourceGridRow, column: NxResourceGridColumn) {
  if (props.interactiveCells) emit('cell-click', row, column);
}

const gridStyle = computed(() => ({
  gridTemplateColumns: [
    props.rowHeaderWidth,
    `repeat(${props.columns.length}, minmax(${props.columnMinWidth}, 1fr))`,
    hasSummary.value ? 'max-content' : '',
  ]
    .filter(Boolean)
    .join(' '),
}));

/** Grid column of the summary column (after the day columns). */
const summaryColumn = computed(() => props.columns.length + 2);

const legendSteps = computed(() => [
  { cls: 'level-0', label: props.legendLabels.free },
  { cls: 'level-1', label: props.legendLabels.low },
  { cls: 'level-2', label: props.legendLabels.medium },
  { cls: 'level-3', label: props.legendLabels.full },
  { cls: 'level-over', label: props.legendLabels.over },
  { cls: 'is-non-working', label: props.legendLabels.nonWorking },
]);
</script>

<template>
  <div ref="rootRef" class="nx-resource-grid">
    <div class="nx-resource-grid__scroll">
      <div class="nx-resource-grid__grid" role="table" :aria-label="ariaLabel" :style="gridStyle">
        <div class="nx-resource-grid__row" role="row">
          <div class="nx-resource-grid__corner" role="columnheader">{{ rowHeader }}</div>
          <div
            v-for="(column, c) in columns"
            :id="`${gridId}-col-${c}`"
            :key="column.key"
            class="nx-resource-grid__col-head"
            :class="{ 'is-non-working': column.nonWorking, 'is-current': column.current }"
            role="columnheader"
            :style="{ gridColumn: c + 2 }"
          >
            <slot name="column-label" :column="column">
              <span class="nx-resource-grid__col-label">{{ column.label }}</span>
              <span v-if="column.sublabel" class="nx-resource-grid__sub">
                {{ column.sublabel }}
              </span>
            </slot>
          </div>
          <div
            v-if="hasSummary"
            class="nx-resource-grid__col-head nx-resource-grid__summary-head"
            role="columnheader"
            :style="{ gridColumn: summaryColumn }"
          >
            {{ summaryLabel }}
          </div>
        </div>

        <div v-for="laid in layout" :key="laid.row.id" class="nx-resource-grid__row" role="row">
          <div
            class="nx-resource-grid__row-head"
            role="rowheader"
            :style="{ gridRow: `${laid.gridRow} / span ${laid.lanes}`, gridColumn: 1 }"
          >
            <slot name="row-label" :row="laid.row">
              <span class="nx-resource-grid__row-label">{{ laid.row.label }}</span>
              <span v-if="laid.row.sublabel" class="nx-resource-grid__sub">
                {{ laid.row.sublabel }}
              </span>
            </slot>
          </div>

          <div
            v-for="(column, c) in columns"
            :key="column.key"
            class="nx-resource-grid__cell"
            :class="[cellClasses(laid.row, column), { 'is-interactive': interactiveCells }]"
            role="cell"
            :aria-label="cellLabel(laid.row, column)"
            :title="cellOf(laid.row, column)?.title"
            :tabindex="interactiveCells ? 0 : undefined"
            :style="{ gridRow: `${laid.gridRow} / span ${laid.lanes}`, gridColumn: c + 2 }"
            @click="onCellClick(laid.row, column)"
            @keydown="onCellKeydown($event, laid.row, column)"
            @dragover="onCellDragOver($event, laid.row, c)"
            @dragleave="dropCell = null"
            @drop.prevent="onCellDrop(laid.row, c)"
          >
            <slot name="cell" :row="laid.row" :column="column" :cell="cellOf(laid.row, column)">
              <span v-if="cellOf(laid.row, column)?.text" class="nx-resource-grid__cell-text">
                {{ cellOf(laid.row, column)?.text }}
              </span>
            </slot>
          </div>

          <div
            v-if="hasSummary"
            class="nx-resource-grid__figure is-summary"
            :class="`tone-${summaryOf(laid.row)?.tone ?? 'neutral'}`"
            role="cell"
            :title="summaryOf(laid.row)?.title"
            :style="{ gridRow: `${laid.gridRow} / span ${laid.lanes}`, gridColumn: summaryColumn }"
          >
            {{ summaryOf(laid.row)?.text }}
          </div>

          <div
            v-for="placed in laid.placed"
            :key="placed.block.id"
            class="nx-resource-grid__block"
            :class="[
              blockToneClass(placed.block),
              {
                'is-locked': placed.block.locked,
                'is-dragging': dragging?.block.id === placed.block.id,
              },
            ]"
            tabindex="0"
            draggable="true"
            :aria-label="blockLabel(placed.block)"
            :title="blockLabel(placed.block)"
            :data-grid-block="placed.block.id"
            :style="{
              gridRow: laid.gridRow + placed.lane,
              gridColumn: `${placed.column + 2} / span ${placed.span}`,
              ...(isHue(placed.block.tone) ? hueVars(placed.block.tone, '--block-hue') : {}),
            }"
            @click="emit('block-open', placed.block)"
            @dragstart="onBlockDragStart($event, placed)"
            @dragend="onDragEnd"
            @dragover="onBlockDragOver($event, placed, laid)"
            @drop.prevent="onBlockDrop($event, placed, laid)"
            @keydown="onBlockKeydown($event, placed)"
          >
            <slot name="block" :block="placed.block">
              <LockClosedIcon
                v-if="placed.block.locked"
                filled
                class="nxt-icon-xs nx-resource-grid__lock"
              />
              <span class="nx-resource-grid__block-label">{{ placed.block.label }}</span>
            </slot>
          </div>
        </div>

        <div v-if="hasFooter" class="nx-resource-grid__row" role="row">
          <div
            class="nx-resource-grid__row-head nx-resource-grid__footer-head"
            role="rowheader"
            :style="{ gridRow: footerGridRow, gridColumn: 1 }"
          >
            {{ footerLabel }}
          </div>
          <div
            v-for="(column, c) in columns"
            :key="column.key"
            class="nx-resource-grid__figure is-footer"
            :class="`tone-${footerOf(column)?.tone ?? 'neutral'}`"
            role="cell"
            :title="footerOf(column)?.title"
            :style="{ gridRow: footerGridRow, gridColumn: c + 2 }"
          >
            {{ footerOf(column)?.text }}
          </div>
          <div
            v-if="hasSummary"
            class="nx-resource-grid__figure is-footer"
            role="cell"
            :style="{ gridRow: footerGridRow, gridColumn: summaryColumn }"
          />
        </div>
      </div>
    </div>

    <ul v-if="legend" class="nx-resource-grid__legend">
      <li v-for="step in legendSteps" :key="step.cls">
        <span class="nx-resource-grid__swatch" :class="step.cls" aria-hidden="true" />
        {{ step.label }}
      </li>
    </ul>

    <div class="nx-resource-grid__live" aria-live="polite" role="status">{{ announcement }}</div>
  </div>
</template>

<style scoped>
.nx-resource-grid {
  --resource-grid-lane: var(--nxt-control-size-md);
  --resource-grid-hatch: repeating-linear-gradient(
    135deg,
    color-mix(in srgb, var(--nxt-border-strong) 70%, transparent) 0 var(--nxt-hairline),
    transparent var(--nxt-hairline) calc(var(--nxt-size-unit) * 0.375)
  );
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  min-width: 0;
  position: relative;
}

.nx-resource-grid__scroll {
  overflow: auto;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-bg);
}

.nx-resource-grid__grid {
  display: grid;
  grid-auto-rows: minmax(var(--resource-grid-lane), auto);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg);
  min-width: max-content;
}

/* Rows exist for the accessibility tree only; their children sit on the grid. */
.nx-resource-grid__row {
  display: contents;
}

.nx-resource-grid__corner,
.nx-resource-grid__col-head,
.nx-resource-grid__row-head {
  background: var(--nxt-surface-raised-bg);
  z-index: var(--nxt-z-raised);
}

.nx-resource-grid__corner,
.nx-resource-grid__col-head {
  position: sticky;
  top: 0;
  grid-row: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg-muted);
}
.nx-resource-grid__corner {
  left: 0;
  grid-column: 1;
  z-index: calc(var(--nxt-z-raised) + 2);
}
.nx-resource-grid__col-head {
  align-items: center;
  text-align: center;
  border-left: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-resource-grid__col-head.is-non-working {
  background-image: var(--resource-grid-hatch);
}
.nx-resource-grid__col-head.is-current {
  color: var(--nxt-brand-bg);
  box-shadow: inset 0 calc(var(--nxt-hairline) * -2) 0 var(--nxt-brand-bg);
}

.nx-resource-grid__row-head {
  position: sticky;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: var(--nxt-space-2xs) var(--nxt-space-sm);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border-subtle);
  border-right: var(--nxt-hairline) solid var(--nxt-border);
  min-width: 0;
}
.nx-resource-grid__row-label,
.nx-resource-grid__col-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.nx-resource-grid__row-label {
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-resource-grid__sub {
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-regular);
  color: var(--nxt-fg-subtle);
  white-space: nowrap;
}

/* ── Cells: capacity colour steps ─────────────────────────────────────────── */
.nx-resource-grid__cell,
.nx-resource-grid__swatch {
  --resource-grid-fill: var(--nxt-surface-bg);
  background-color: var(--resource-grid-fill);
}
.nx-resource-grid__cell {
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: var(--nxt-hairline) solid var(--nxt-border-subtle);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border-subtle);
  font-variant-numeric: tabular-nums;
  outline: none;
}
.nx-resource-grid__cell.is-interactive {
  cursor: pointer;
}
.nx-resource-grid__cell.is-interactive:hover {
  box-shadow: inset 0 0 0 var(--nxt-hairline) var(--nxt-border-strong);
}
.nx-resource-grid__cell:focus-visible {
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.level-1 {
  --resource-grid-fill: color-mix(in srgb, var(--nxt-brand-bg) 12%, var(--nxt-surface-bg));
}
.level-2 {
  --resource-grid-fill: color-mix(in srgb, var(--nxt-brand-bg) 28%, var(--nxt-surface-bg));
}
.level-3 {
  --resource-grid-fill: color-mix(in srgb, var(--nxt-brand-bg) 45%, var(--nxt-surface-bg));
}
/* Mixed from the solid danger step like the brand steps above: the subtle
   danger surface is near-black in dark mode and read weaker than „full". */
.level-over {
  --resource-grid-fill: color-mix(in srgb, var(--nxt-danger-bg) 28%, var(--nxt-surface-bg));
  color: var(--nxt-danger-subtle-fg);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-resource-grid__cell.level-over {
  box-shadow: inset 0 0 0 var(--nxt-hairline) var(--nxt-danger-border);
}
.is-non-working {
  background-image: var(--resource-grid-hatch);
}
.nx-resource-grid__cell.is-non-working {
  color: var(--nxt-fg-subtle);
}
.nx-resource-grid__cell.is-current {
  box-shadow:
    inset calc(var(--nxt-hairline) * 2) 0 0 color-mix(in srgb, var(--nxt-brand-bg) 40%, transparent),
    inset calc(var(--nxt-hairline) * -2) 0 0
      color-mix(in srgb, var(--nxt-brand-bg) 40%, transparent);
}
.nx-resource-grid__cell.is-drop-ok {
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-brand-bg);
}
.nx-resource-grid__cell.is-drop-rejected {
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-danger-border);
  cursor: no-drop;
}

/* ── Blocks ────────────────────────────────────────────────────────────────── */
.nx-resource-grid__block {
  --resource-grid-block-bg: var(--nxt-brand-subtle-bg);
  --resource-grid-block-edge: var(--nxt-brand-bg);
  --resource-grid-block-fg: var(--nxt-brand-subtle-fg);
  z-index: var(--nxt-z-raised);
  align-self: center;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  margin: var(--nxt-space-2xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  border-radius: var(--nxt-radius-sm);
  border: var(--nxt-hairline) solid
    color-mix(in srgb, var(--resource-grid-block-edge) 45%, transparent);
  border-left: calc(var(--nxt-hairline) * 3) solid var(--resource-grid-block-edge);
  background: var(--resource-grid-block-bg);
  color: var(--resource-grid-block-fg);
  box-shadow: var(--nxt-shadow-sm);
  cursor: grab;
  outline: none;
}
.nx-resource-grid__block:focus-visible {
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-resource-grid__block.is-dragging {
  opacity: 0.4;
}
.nx-resource-grid__block.is-locked {
  cursor: default;
  border-style: dashed;
  border-left-style: solid;
}
.nx-resource-grid__block.tone-info {
  --resource-grid-block-bg: var(--nxt-info-subtle-bg);
  --resource-grid-block-edge: var(--nxt-info-bg);
  --resource-grid-block-fg: var(--nxt-info-subtle-fg);
}
.nx-resource-grid__block.tone-success {
  --resource-grid-block-bg: var(--nxt-success-subtle-bg);
  --resource-grid-block-edge: var(--nxt-success-bg);
  --resource-grid-block-fg: var(--nxt-success-subtle-fg);
}
.nx-resource-grid__block.tone-warning {
  --resource-grid-block-bg: var(--nxt-warning-subtle-bg);
  --resource-grid-block-edge: var(--nxt-warning-bg);
  --resource-grid-block-fg: var(--nxt-warning-subtle-fg);
}
.nx-resource-grid__block.tone-danger {
  --resource-grid-block-bg: var(--nxt-danger-subtle-bg);
  --resource-grid-block-edge: var(--nxt-danger-bg);
  --resource-grid-block-fg: var(--nxt-danger-subtle-fg);
}
.nx-resource-grid__block.has-hue {
  --resource-grid-block-bg: var(--block-hue-subtle-bg);
  --resource-grid-block-edge: var(--block-hue);
  --resource-grid-block-fg: var(--block-hue-fg);
}
.nx-resource-grid__block.tone-neutral {
  --resource-grid-block-bg: var(--nxt-surface-raised-bg);
  --resource-grid-block-edge: var(--nxt-border-strong);
  --resource-grid-block-fg: var(--nxt-fg);
}
.nx-resource-grid__block-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: var(--nxt-font-weight-medium);
}
.nx-resource-grid__lock {
  flex-shrink: 0;
}

/* ── Summary column and footer row ───────────────────────────────────────── */
.nx-resource-grid__figure {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--nxt-space-xs);
  font-weight: var(--nxt-font-weight-semibold);
  font-variant-numeric: tabular-nums;
  background: var(--nxt-surface-raised-bg);
  white-space: nowrap;
}
.nx-resource-grid__figure.is-summary {
  justify-content: flex-end;
  border-left: var(--nxt-hairline) solid var(--nxt-border);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-resource-grid__figure.is-footer,
.nx-resource-grid__footer-head {
  position: sticky;
  bottom: 0;
  z-index: var(--nxt-z-raised);
  border-top: var(--nxt-hairline) solid var(--nxt-border);
  border-left: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-resource-grid__footer-head {
  left: 0;
  z-index: calc(var(--nxt-z-raised) + 2);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg-muted);
}
.nx-resource-grid__summary-head {
  border-left: var(--nxt-hairline) solid var(--nxt-border);
}
.nx-resource-grid__figure.tone-success {
  color: var(--nxt-success-subtle-fg);
  background: var(--nxt-success-subtle-bg);
}
.nx-resource-grid__figure.tone-warning {
  color: var(--nxt-warning-subtle-fg);
  background: var(--nxt-warning-subtle-bg);
}
.nx-resource-grid__figure.tone-danger {
  color: var(--nxt-danger-subtle-fg);
  background: var(--nxt-danger-subtle-bg);
}

/* ── Legend ────────────────────────────────────────────────────────────────── */
.nx-resource-grid__legend {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--nxt-space-2xs) var(--nxt-space-sm);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
}
.nx-resource-grid__legend li {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
}
.nx-resource-grid__swatch {
  width: calc(var(--nxt-size-unit) * 0.75);
  height: calc(var(--nxt-size-unit) * 0.75);
  border-radius: var(--nxt-radius-sm);
  border: var(--nxt-hairline) solid var(--nxt-border);
}

.nx-resource-grid__live {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
</style>
